import type { MatrixClient, MatrixEvent, Room } from 'matrix-js-sdk';
import { RoomEvent } from 'matrix-js-sdk';

type Listener = () => void;

/**
 * Store für die Timeline eines einzelnen Rooms.
 */
class RoomTimelineStore {
  private listeners = new Set<Listener>();
  private room: Room | null = null;
  private events: MatrixEvent[] = [];
  private isSetup = false;

  constructor(
    private readonly roomId: string,
    private readonly client: MatrixClient,
  ) {
    this.setup();
  }

  private setup(): void {
    if (this.isSetup) return;

    this.room = this.client.getRoom(this.roomId);
    if (!this.room) {
      this.events = [];
      return;
    }

    this.events = this.room.getLiveTimeline().getEvents();
    this.room.on(RoomEvent.Timeline, this.handleTimelineChange);
    this.room.on(RoomEvent.Redaction, this.handleTimelineChange);
    this.isSetup = true;
  }

  private handleTimelineChange = (): void => {
    if (!this.room) {
      this.events = [];
    } else {
      this.events = [...this.room.getLiveTimeline().getEvents()];
    }
    this.emitChange();
  };

  getSnapshot = (): MatrixEvent[] => {
    return this.events;
  };

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private emitChange(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  /**
   * Cleanup-Funktion zum Entfernen von Event-Listenern.
   */
  destroy(): void {
    if (this.room && this.isSetup) {
      this.room.removeListener(RoomEvent.Timeline, this.handleTimelineChange);
      this.room.removeListener(RoomEvent.Redaction, this.handleTimelineChange);
    }
    this.listeners.clear();
    this.isSetup = false;
  }
}

/**
 * Store-Manager für Room Timelines.
 * Verwaltet Stores pro Room und kümmert sich um Cleanup.
 */
class RoomTimelineStoreManager {
  private stores = new Map<string, RoomTimelineStore>();
  private client: MatrixClient | null = null;

  setClient(client: MatrixClient | null): void {
    // Cleanup alte Stores
    if (this.client !== client) {
      this.cleanup();
    }
    this.client = client;
  }

  getStore(roomId: string): RoomTimelineStore | null {
    if (!this.client) return null;

    const key = roomId;

    if (!this.stores.has(key)) {
      const store = new RoomTimelineStore(roomId, this.client);
      this.stores.set(key, store);
    }

    return this.stores.get(key) ?? null;
  }

  /**
   * Cleanup nicht mehr verwendeter Stores.
   */
  cleanup(): void {
    for (const store of this.stores.values()) {
      store.destroy();
    }
    this.stores.clear();
  }

  /**
   * Cleanup eines spezifischen Stores.
   */
  cleanupStore(roomId: string): void {
    const store = this.stores.get(roomId);
    if (store) {
      store.destroy();
      this.stores.delete(roomId);
    }
  }
}

// Singleton-Instanz
export const roomTimelineStoreManager = new RoomTimelineStoreManager();
